import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Typography,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Badge
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { de } from 'date-fns/locale';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CompletedIcon,
  Schedule as ScheduledIcon,
  Cancel as CancelledIcon,
  HourglassEmpty as ActiveIcon,
  Person as DriverIcon,
  DirectionsCar as CarIcon,
  Event as CalendarIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { fetchShifts, deleteShift } from '../../redux/slices/shiftsSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

// Helper-Funktionen für StatusChip
const getIconForStatus = (status) => {
  switch (status) {
    case 'completed':
      return <CompletedIcon />;
    case 'active':
      return <ActiveIcon />;
    case 'scheduled':
      return <ScheduledIcon />;
    case 'cancelled':
      return <CancelledIcon />;
    default:
      return <ScheduledIcon />;
  }
};

const getColorForStatus = (status, theme) => {
  switch (status) {
    case 'completed':
      return theme.palette.success.main;
    case 'active':
      return theme.palette.primary.main;
    case 'scheduled':
      return theme.palette.info.main;
    case 'cancelled':
      return theme.palette.error.main;
    default:
      return undefined;
  }
};

// Vereinfachte StatusChip ohne verschachtelte Arrow-Funktionen
const StatusChip = styled(Chip)(({ theme, status }) => ({
  color: status === 'default' ? 'inherit' : 'white',
  backgroundColor: getColorForStatus(status, theme),
  '& .MuiSvgIcon-root': {
    fontSize: '1rem',
  }
}));

const ShiftsList = () => {
  const dispatch = useDispatch();
  const { shifts, isLoading, error } = useSelector(state => state.shifts);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(null);
  const [sortBy, setSortBy] = useState('start_time');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuShift, setMenuShift] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  
  useEffect(() => {
    const params = {};
    if (dateFilter) {
      params.date = dateFilter.toISOString();
    }
    dispatch(fetchShifts(params));
  }, [dispatch, dateFilter]);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };
  
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };
  
  const handleDateFilterChange = (date) => {
    setDateFilter(date);
    setPage(0);
  };
  
  const handleSortByChange = (event) => {
    setSortBy(event.target.value);
    setPage(0);
  };
  
  const handleOpenMenu = (event, shift) => {
    setMenuAnchor(event.currentTarget);
    setMenuShift(shift);
  };
  
  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setMenuShift(null);
  };
  
  const handleDeleteClick = (shift) => {
    setSelectedShift(shift);
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };
  
  const handleDeleteConfirm = () => {
    if (selectedShift) {
      dispatch(deleteShift(selectedShift.id));
      setDeleteDialogOpen(false);
      setSelectedShift(null);
    }
  };
  
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedShift(null);
  };
  
  // Filter and sort data
  const filteredData = shifts.filter(shift => {
    // Filter by search term (driver name, vehicle model, etc.)
    const searchMatch = 
      shift.driver?.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.vehicle?.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.vehicle?.license_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by status
    const statusMatch = statusFilter === 'all' || shift.status === statusFilter;
    
    return searchMatch && statusMatch;
  }).sort((a, b) => {
    // Sort data
    switch (sortBy) {
      case 'start_time':
        return new Date(b.start_time) - new Date(a.start_time);
      case 'end_time':
        return new Date(b.end_time) - new Date(a.end_time);
      case 'driver':
        return (a.driver?.user?.name || '').localeCompare(b.driver?.user?.name || '');
      case 'status':
        // Sort by status priority
        const statusPriority = { 
          'active': 3, 
          'scheduled': 2,
          'completed': 1, 
          'cancelled': 0 
        };
        return statusPriority[b.status] - statusPriority[a.status];
      default:
        return 0;
    }
  });
  
  // Paginate data
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };
  
  const formatTimeOnly = (dateString) => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '-';
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${diffHrs} Std. ${diffMins} Min.`;
  };
  
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return `${parseFloat(value).toFixed(2)} €`;
  };
  
  const translateStatus = (status) => {
    const statusMap = {
      'scheduled': 'Geplant',
      'active': 'Aktiv',
      'completed': 'Abgeschlossen',
      'cancelled': 'Storniert'
    };
    
    return statusMap[status] || status;
  };
  
  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };
  
  if (isLoading) {
    return <Loading message="Schichten werden geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Schichten"
        message={error}
        onRetry={() => dispatch(fetchShifts())}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Schichtplanung"
        description="Übersicht und Planung aller Fahrerschichten"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Schichten', path: '/schichten' }
        ]}
        actionButton={{
          label: 'Schicht hinzufügen',
          icon: <AddIcon />,
          path: '/schichten/neu'
        }}
      />
      
      {/* Filter und Suche */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Suche nach Fahrer oder Fahrzeug"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">Alle Status</MenuItem>
                <MenuItem value="scheduled">Geplant</MenuItem>
                <MenuItem value="active">Aktiv</MenuItem>
                <MenuItem value="completed">Abgeschlossen</MenuItem>
                <MenuItem value="cancelled">Storniert</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
              <DatePicker
                label="Nach Datum filtern"
                value={dateFilter}
                onChange={handleDateFilterChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
                clearable
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel id="sort-by-label">Sortieren nach</InputLabel>
              <Select
                labelId="sort-by-label"
                id="sort-by"
                value={sortBy}
                label="Sortieren nach"
                onChange={handleSortByChange}
              >
                <MenuItem value="start_time">Startzeit</MenuItem>
                <MenuItem value="end_time">Endzeit</MenuItem>
                <MenuItem value="driver">Fahrername</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {filteredData.length} Schichten gefunden
              </Typography>
              <Button 
                variant="outlined" 
                color="primary" 
                component={Link} 
                to="/schichten/kalender"
                startIcon={<CalendarIcon />}
              >
                Kalenderansicht
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Schichtentabelle */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="Schichtentabelle">
          <TableHead>
            <TableRow>
              <TableCell>Datum</TableCell>
              <TableCell>Zeit</TableCell>
              <TableCell>Fahrer</TableCell>
              <TableCell>Fahrzeug</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Fahrten</TableCell>
              <TableCell align="center">Umsatz</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((shift) => (
                <StyledTableRow key={shift.id}>
                  <TableCell>
                    {formatDateOnly(shift.start_time)}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {formatTimeOnly(shift.start_time)} - {formatTimeOnly(shift.end_time)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Dauer: {formatDuration(shift.start_time, shift.end_time)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {shift.driver ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Badge 
                          color={shift.driver.status === 'available' ? 'success' : 
                                 shift.driver.status === 'busy' ? 'primary' : 'default'} 
                          variant="dot"
                        >
                          <DriverIcon />
                        </Badge>
                        <Box>
                          <Typography variant="body2">
                            {shift.driver.user?.name || 'Unbekannt'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {shift.driver.user?.phone || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Nicht zugewiesen
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {shift.vehicle ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CarIcon />
                        <Box>
                          <Typography variant="body2">
                            {shift.vehicle.model || 'Unbekannt'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {shift.vehicle.license_plate || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Nicht zugewiesen
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <StatusChip 
                      label={translateStatus(shift.status)} 
                      status={shift.status}
                      size="small"
                      icon={
                        shift.status === 'completed' ? <CompletedIcon /> : 
                        shift.status === 'active' ? <ActiveIcon /> : 
                        shift.status === 'scheduled' ? <ScheduledIcon /> :
                        <CancelledIcon />
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    {shift.total_rides || 0}
                  </TableCell>
                  <TableCell align="center">
                    {formatCurrency(shift.total_earnings)}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`shift-menu-${shift.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, shift)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    Keine Schichten gefunden
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={
            ({ from, to, count }) => `${from}–${to} von ${count}`
          }
        />
      </TableContainer>
      
      {/* Aktions-Menü */}
      <Menu
        id={menuShift ? `shift-menu-${menuShift.id}` : undefined}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          component={Link} 
          to={menuShift ? `/schichten/${menuShift.id}` : '#'} 
          onClick={handleCloseMenu}
        >
          Schicht anzeigen
        </MenuItem>
        <MenuItem 
          component={Link} 
          to={menuShift ? `/schichten/${menuShift.id}/bearbeiten` : '#'} 
          onClick={handleCloseMenu}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Bearbeiten
        </MenuItem>
        <MenuItem 
          onClick={() => menuShift && handleDeleteClick(menuShift)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Löschen
        </MenuItem>
      </Menu>
      
      {/* Löschen-Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Schicht löschen"
        content={`Möchten Sie die Schicht vom ${selectedShift ? formatDateOnly(selectedShift.start_time) : ''} für ${selectedShift?.driver?.user?.name || 'unbekannt'} wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default ShiftsList;